import {
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    addMonths,
    addYears,
    endOfMonth,
    endOfYear,
    isSameMonth,
    isSameYear,
    isThisMonth,
    subMonths,
    subYears
} from 'date-fns';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LsqValidators } from '../../../validators/lsq-validators';
import { BaseFormControlComponent } from '../../forms/base-form-control.component';
import { DatePickerType } from './constants/date-picker-type';
import { IDateRange } from './models/date-range';

/**
 * @group Forms
 * @component Date Picker
 */
@Component({
    selector: 'date-picker',
    template: `
        <ng-container
            *ngIf="(type === datePickerType.Single || type === datePickerType.Month) && !!dateForm"
        >
            <calendar
                class="calendar single-calendar"
                [date]="dateModel"
                (dateChange)="handleDateChange($event)"
                [type]="type"
                [upperLimit]="upperLimit"
                [lowerLimit]="lowerLimit"
                [allowFuture]="allowFuture"
                [leftArrowSvgSrc]="leftArrowSvgSrc"
                [rightArrowSvgSrc]="rightArrowSvgSrc"
                (dayClick)="handleDayClick($event)"
            >
            </calendar>
        </ng-container>

        <ng-container *ngIf="type === datePickerType.Range || type === datePickerType.MonthRange">
            <ng-container *ngIf="dualCalendar">
                <div class="date-picker-header" role="navigation">
                    <div class="btn-left-container">
                        <button (click)="goToPreviousRange()">
                            <svg-icon *ngIf="!!leftArrowSvgSrc" [src]="leftArrowSvgSrc"> </svg-icon>
                            <span *ngIf="!leftArrowSvgSrc">←</span>
                        </button>
                    </div>
                    <div
                        class="btn-right-container"
                        [ngClass]="{ 'btn-container-disabled': !nextIsAvailable() }"
                    >
                        <button [disabled]="!nextIsAvailable()" (click)="goToNextRange()">
                            <svg-icon *ngIf="!!rightArrowSvgSrc" [src]="rightArrowSvgSrc">
                            </svg-icon>
                            <span *ngIf="!rightArrowSvgSrc">→</span>
                        </button>
                    </div>
                </div>
                <div class="dual-calendar-container">
                    <calendar
                        class="calendar dual-calendar"
                        [dateRange]="rangeModel"
                        (dateRangeChange)="handleRangeChange($event)"
                        [type]="type"
                        [month]="dualCalendarMonthRange.start"
                        [year]="dualCalendarYearRange.start"
                        [upperLimit]="upperLimit"
                        [lowerLimit]="lowerLimit"
                        [allowFuture]="allowFuture"
                        [isDualCalendarStart]="true"
                        [isSettingDateRangeStart]="isSettingDateRangeStart"
                        [isSettingDateRangeEnd]="isSettingDateRangeEnd"
                        (dayClick)="handleDayClick($event)"
                        [(rangeHovered)]="rangeHoveredModel"
                    >
                    </calendar>
                    <calendar
                        class="calendar dual-calendar"
                        [dateRange]="rangeModel"
                        (dateRangeChange)="handleRangeChange($event)"
                        [type]="type"
                        [month]="dualCalendarMonthRange.end"
                        [year]="dualCalendarYearRange.end"
                        [upperLimit]="upperLimit"
                        [lowerLimit]="lowerLimit"
                        [allowFuture]="allowFuture"
                        [isDualCalendarEnd]="true"
                        [isSettingDateRangeStart]="isSettingDateRangeStart"
                        [isSettingDateRangeEnd]="isSettingDateRangeEnd"
                        (dayClick)="handleDayClick($event)"
                        [(rangeHovered)]="rangeHoveredModel"
                    >
                    </calendar>
                </div>
            </ng-container>
            <ng-container *ngIf="!dualCalendar">
                <calendar
                    class="calendar"
                    [dateRange]="rangeModel"
                    (dateRangeChange)="handleRangeChange($event)"
                    [type]="type"
                    [month]="rangeModel?.start"
                    [year]="rangeModel?.start"
                    [upperLimit]="upperLimit"
                    [lowerLimit]="lowerLimit"
                    [allowFuture]="allowFuture"
                    [isSettingDateRangeStart]="isSettingDateRangeStart"
                    [isSettingDateRangeEnd]="isSettingDateRangeEnd"
                    (dayClick)="handleDayClick($event)"
                    [(rangeHovered)]="rangeHoveredModel"
                    [leftArrowSvgSrc]="leftArrowSvgSrc"
                    [rightArrowSvgSrc]="rightArrowSvgSrc"
                >
                </calendar>
            </ng-container>
        </ng-container>
    `,
    styleUrls: ['./date-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        }
    ]
})
export class DatePickerComponent extends BaseFormControlComponent<Date | IDateRange>
    implements OnChanges {
    @Input() public type: DatePickerType = DatePickerType.Single;
    @Input() public lowerLimit: Date;
    @Input() public upperLimit: Date;
    @Input() public allowFuture = false;
    @Input() public dualCalendar = true;
    @Input() public leftArrowSvgSrc: string;
    @Input() public rightArrowSvgSrc: string;
    @Output() public dayClick = new EventEmitter<Date>();
    @Output() public validityChange = new EventEmitter<{ [key: string]: any }>();

    public dateForm = new FormGroup({
        date: new FormControl('')
    });
    public rangeForm = new FormGroup({
        range: new FormControl('')
    });
    public dateModel: Date;
    public rangeModel: IDateRange;
    public rangeHoveredModel: IDateRange;
    public isSettingDateRangeStart = true;
    public isSettingDateRangeEnd = false;
    public dualCalendarMonthRange: IDateRange = {
        start: null as Date,
        end: null as Date
    };
    public dualCalendarYearRange: IDateRange = {
        start: null as Date,
        end: null as Date
    };
    public datePickerType = DatePickerType;
    private _now = new Date();
    private _init$ = new Subject<any>();

    public ngOnChanges(changes: SimpleChanges): void {
        if (!!changes.type) {
            this._init();
            this.modelChangeSubject
                .pipe(takeUntil<Date | IDateRange>(this._init$))
                .subscribe((value) => {
                    if (this._isSingleDate()) {
                        this.dateModel = value as Date;
                    }
                    if (this._isRange()) {
                        this.rangeModel = value as IDateRange;
                    }
                });
        }
    }

    private _init(): void {
        this._init$.next();

        setTimeout(() => {
            let defaultValue = this.model;
            const validators = [];

            if (this._isSingleDate()) {
                if (!!this.lowerLimit) {
                    validators.push(LsqValidators.minDate(this.lowerLimit));
                }
                if (!!this.upperLimit) {
                    validators.push(LsqValidators.maxDate(this.upperLimit));
                }

                if (!defaultValue) {
                    defaultValue = this._now;
                }

                this.dateModel = defaultValue as Date;
                this.dateForm.setControl('date', new FormControl(defaultValue, validators));
                this.dateForm.valueChanges
                    .pipe(
                        takeUntil(this._init$),
                        filter(() => this.dateForm.valid)
                    )
                    .subscribe(({ date }) => {
                        this.handleChange(date);
                    });
                this.dateForm.statusChanges.subscribe(() => {
                    this.validityChange.emit(this.dateForm.get('date').errors);
                });
                this.validityChange.emit(this.dateForm.get('date').errors);
            }

            if (this._isRange()) {
                if (!!this.lowerLimit) {
                    validators.push(LsqValidators.minDateRange(this.lowerLimit));
                }
                if (!!this.upperLimit) {
                    validators.push(LsqValidators.maxDateRange(this.upperLimit));
                }

                if (!defaultValue) {
                    if (!!this.model) {
                        defaultValue = {
                            start: (this.model as IDateRange).start,
                            end: (this.model as IDateRange).end
                        };
                    } else {
                        defaultValue = {
                            start: null,
                            end: null
                        };
                    }
                }

                this.rangeModel = defaultValue as IDateRange;
                this._setDualCalendarMonthRange(this.rangeModel);
                this._setDualCalendarYearRange(this.rangeModel);
                this.rangeForm.setControl('range', new FormControl(defaultValue, validators));
                this.rangeForm.valueChanges
                    .pipe(
                        takeUntil(this._init$),
                        filter(() => this.rangeForm.valid)
                    )
                    .subscribe(({ range }: { range: IDateRange }) => {
                        this.handleChange(range);
                    });

                this.rangeForm.statusChanges.subscribe(() => {
                    this.validityChange.emit(this.rangeForm.get('range').errors);
                });
                this.validityChange.emit(this.rangeForm.get('range').errors);
            }
        });

        // Set `upperLimit` equal to today if `allowFuture` is false.
        if (!this.allowFuture) {
            this.upperLimit = this._now;
        }
    }

    private _setDualCalendarMonthRange(range: IDateRange): void {
        if (range.start) {
            let newRangeStart = range.start;
            if (isSameMonth(range.start, range.end)) {
                newRangeStart = subMonths(range.start, 1);
            }
            this.dualCalendarMonthRange = {
                start: newRangeStart,
                end:
                    !!range.end && isSameMonth(newRangeStart, range.end) && !isThisMonth(range.end)
                        ? addMonths(range.end, 1)
                        : range.end
            };
        } else {
            this.dualCalendarMonthRange = {
                start: subMonths(this._now, 1),
                end: this._now
            };
        }
    }

    private _setDualCalendarYearRange(range: IDateRange): void {
        if (!!range.start) {
            this.dualCalendarYearRange = {
                start: range.start,
                end:
                    !!range.end && isSameYear(range.start, range.end)
                        ? addYears(range.end, 1)
                        : range.end
            };
        } else {
            this.dualCalendarYearRange = {
                start: this._now,
                end: addYears(this._now, 1)
            };
        }
    }

    // Event handlers.
    public handleDateChange(date: Date): void {
        this.dateModel = date;
        this.dateForm.setValue({ date: this.dateModel });
    }

    public handleRangeChange(dateRange: IDateRange): void {
        this.rangeModel = dateRange;
        if (!dateRange.end) {
            this.isSettingDateRangeStart = false;
            this.isSettingDateRangeEnd = true;
        } else if (dateRange.end < dateRange.start) {
            dateRange.start = dateRange.end;
            this.isSettingDateRangeStart = false;
            this.isSettingDateRangeEnd = true;
        } else if (this.isSettingDateRangeStart) {
            this.isSettingDateRangeStart = false;
            this.isSettingDateRangeEnd = true;
        } else if (this.isSettingDateRangeEnd) {
            this.isSettingDateRangeEnd = false;
            this.isSettingDateRangeStart = true;
        }
        this.rangeForm.setValue({ range: this.rangeModel });
    }

    public handleDayClick(date: Date): void {
        this.dayClick.emit(date);
    }

    // Override of BaseFormControlComponent#writeValue.
    // Intercepts incoming values (acts like a setter for `ngModel`).
    public writeValue(model: Date | IDateRange = null): void {
        if (!!this.rangeForm && this.type === DatePickerType.Range) {
            if (
                !!model &&
                typeof (model as IDateRange).start !== 'undefined' &&
                typeof (model as IDateRange).end !== 'undefined'
            ) {
                this.rangeModel = model as IDateRange;
            } else {
                this.rangeModel = {
                    start: null,
                    end: null
                };
            }
            this.rangeHoveredModel = this.rangeModel;
            this.rangeForm.setValue({ range: this.rangeModel });
            this._setDualCalendarMonthRange(this.rangeModel);
            this._setDualCalendarYearRange(this.rangeModel);
            this.isSettingDateRangeStart = true;
            this.isSettingDateRangeEnd = false;
        } else if (!!this.dateForm && this.type !== DatePickerType.Range) {
            this.dateForm.setValue({ date: model });
        }
        super.writeValue(model);
    }

    // Boolean methods.
    public nextIsAvailable(): boolean {
        if (this.type === DatePickerType.MonthRange) {
            return this._nextYearIsAvailable();
        }
        return this._nextMonthIsAvailable();
    }

    private _nextMonthIsAvailable(): boolean {
        if (!this.dualCalendar || !this.upperLimit) {
            return true;
        } else {
            return this.upperLimit > endOfMonth(this.dualCalendarMonthRange.end);
        }
    }

    private _nextYearIsAvailable(): boolean {
        if (!this.dualCalendar || !this.upperLimit) {
            return true;
        } else {
            return this.upperLimit > endOfYear(this.dualCalendarYearRange.end);
        }
    }

    private _isSingleDate(): boolean {
        return this.type === DatePickerType.Single || this.type === DatePickerType.Month;
    }

    private _isRange(): boolean {
        return this.type === DatePickerType.Range || this.type === DatePickerType.MonthRange;
    }

    // Helpers.

    private _goToPreviousMonthRange(): void {
        this.dualCalendarMonthRange = {
            start: subMonths(this.dualCalendarMonthRange.start, 1),
            end: this.dualCalendarMonthRange.start
        };
    }

    private _goToNextMonthRange(): void {
        this.dualCalendarMonthRange = {
            start: this.dualCalendarMonthRange.end,
            end: addMonths(this.dualCalendarMonthRange.end, 1)
        };
    }

    private _goToPreviousYearRange(): void {
        this.dualCalendarYearRange = {
            start: subYears(this.dualCalendarYearRange.start, 1),
            end: this.dualCalendarYearRange.start
        };
    }

    private _goToNextYearRange(): void {
        this.dualCalendarYearRange = {
            start: this.dualCalendarYearRange.end,
            end: addYears(this.dualCalendarYearRange.end, 1)
        };
    }

    // Public API.
    public goToPreviousRange(): void {
        if (this.type === DatePickerType.MonthRange) {
            this._goToPreviousYearRange();
        } else {
            this._goToPreviousMonthRange();
        }
    }

    public goToNextRange(): void {
        if (this.type === DatePickerType.MonthRange) {
            this._goToNextYearRange();
        } else {
            this._goToNextMonthRange();
        }
    }

    public goToToday(): void {
        this.writeValue(this._now);
    }
}
